import React from "react";
import { rosterData } from "../data/mock";
import { ArrowLeft, Award, Shield, ExternalLink, Music, CheckCircle, Star, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link } from "react-router-dom";

// Individual Roster Card Component
const RosterCard = ({ artist, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.1 }}
      data-testid={`roster-card-${artist.id}`}
      className="group bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl overflow-hidden hover:border-[rgba(218,255,1,0.4)] transition-all duration-500 hover:shadow-[0_0_40px_rgba(218,255,1,0.08)]"
    >
      {/* Card Header with Status Indicator */}
      <div className="relative">
        {/* Status Badge */}
        <div className="absolute top-4 right-4 z-10">
          <Badge
            className={`px-3 py-1 text-[9px] tracking-[0.15em] rounded-md font-semibold ${
              artist.status === "EXCLUSIVE"
                ? "bg-[rgb(218,255,1)] text-[rgb(17,17,19)]"
                : artist.status === "PRIORITY"
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                : "bg-[rgb(63,63,63)] text-white"
            }`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {artist.status}
          </Badge>
        </div>

        {/* Artist Visual */}
        <div className={`${artist.photo ? 'aspect-[3/4]' : 'h-48 sm:h-56'} bg-gradient-to-br from-[rgb(40,42,45)] to-[rgb(26,28,30)] flex items-center justify-center relative overflow-hidden`}>
          {/* Abstract Geometric Pattern - shown when no photo */}
          {!artist.photo && (
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-32 h-32 border border-[rgb(218,255,1)] rotate-45 -translate-x-16 -translate-y-16" />
              <div className="absolute bottom-0 right-0 w-40 h-40 border border-[rgb(218,255,1)] rotate-45 translate-x-20 translate-y-20" />
            </div>
          )}
          
          {/* Photo or Monogram */}
          {artist.photo ? (
            <div className="relative w-full h-full">
              <img 
                src={artist.photo} 
                alt={artist.name}
                className="w-full h-full object-cover object-top"
              />
              {/* Verified Badge */}
              {artist.verified && (
                <div className="absolute bottom-3 right-3 w-8 h-8 bg-[rgb(26,28,30)] rounded-full flex items-center justify-center border-2 border-[rgb(74,222,128)] shadow-lg z-10">
                  <CheckCircle size={16} className="text-[rgb(74,222,128)]" />
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <div 
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[rgb(218,255,1)] flex items-center justify-center shadow-[0_0_50px_rgba(218,255,1,0.3)] group-hover:shadow-[0_0_60px_rgba(218,255,1,0.5)] transition-all duration-500"
              >
                <span 
                  className="text-[rgb(17,17,19)] text-4xl sm:text-5xl font-bold"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {artist.initials}
                </span>
              </div>
              {/* Verified Ring */}
              {artist.verified && (
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[rgb(26,28,30)] rounded-full flex items-center justify-center border-2 border-[rgb(74,222,128)]">
                  <CheckCircle size={16} className="text-[rgb(74,222,128)]" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div className="p-5 sm:p-6">
        {/* Name & Title */}
        <h3
          className="text-white text-xl sm:text-2xl font-bold tracking-wide mb-1"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          {artist.name}
        </h3>
        {/* Legal Name & Location */}
        <div className="mb-4">
          {artist.legalName && (
            <p
              className="text-[rgb(100,100,110)] text-[10px] tracking-wide"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              ({artist.legalName})
            </p>
          )}
          {artist.location && (
            <p
              className="text-[rgb(161,161,170)] text-[10px] tracking-wide mt-1 uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              📍 {artist.location}
            </p>
          )}
        </div>

        <p
          className="text-[rgb(218,255,1)] text-xs tracking-[0.15em] font-medium mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {artist.role}
        </p>

        {/* Focus / Specialty Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          {artist.focus ? (
            artist.focus.split('/').map((tag, i) => (
              <span
                key={i}
                className="text-[rgb(161,161,170)] text-[10px] tracking-[0.1em] px-2.5 py-1 bg-[rgb(40,42,45)] rounded-md border border-[rgba(255,255,255,0.05)]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {tag.trim()}
              </span>
            ))
          ) : (
            // Fallback for legacy data if needed
            artist.specialties && artist.specialties.map((specialty, i) => (
              <span
                key={i}
                className="text-[rgb(161,161,170)] text-[10px] tracking-[0.1em] px-2.5 py-1 bg-[rgb(40,42,45)] rounded-md border border-[rgba(255,255,255,0.05)]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {specialty}
              </span>
            ))
          )}
        </div>

        <Separator className="bg-[rgb(63,63,63)] mb-5" />

        {/* Clearance & Stats */}
        <div className="space-y-3">
          {/* Clearance Status */}
          {artist.clearance && (
            <div className="mb-3">
              <p className="text-[rgb(100,100,110)] text-[10px] tracking-[0.2em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                CLEARANCE STATUS
              </p>
              <div className="flex items-center gap-2 text-[rgb(74,222,128)]">
                <Shield size={14} />
                <span className="text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {artist.clearance}
                </span>
              </div>
            </div>
          )}

          {/* Background/Bio with See More */}
          {artist.background && (
            <div className="mb-4">
              <details className="group">
                <summary className="text-[rgb(161,161,170)] text-[11px] leading-relaxed cursor-pointer list-none">
                  <span className="line-clamp-3 group-open:line-clamp-none">
                    {artist.background}
                  </span>
                  <span className="text-[rgb(218,255,1)] text-[10px] mt-1 block group-open:hidden hover:underline">
                    SEE MORE
                  </span>
                </summary>
              </details>
            </div>
          )}

          {/* Notable Singles */}
          {artist.notableSingles && artist.notableSingles.length > 0 && (
            <div>
              <p className="text-[rgb(100,100,110)] text-[10px] tracking-[0.2em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                NOTABLE SINGLES
              </p>
              <div className="flex flex-wrap gap-2">
                {artist.notableSingles.map((single, i) => (
                  <span
                    key={i}
                    className="text-[rgb(218,255,1)] text-[10px] tracking-[0.05em] px-2.5 py-1.5 bg-[rgba(218,255,1,0.08)] border border-[rgba(218,255,1,0.3)] rounded-md"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    "{single}"
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notable Credits */}
          {artist.credits && artist.credits.length > 0 && (
            <div>
              <p className="text-[rgb(100,100,110)] text-[10px] tracking-[0.2em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                NOTABLE CREDITS
              </p>
              <div className="flex flex-wrap gap-2">
                {artist.credits.map((credit, i) => (
                  <span
                    key={i}
                    className="text-white text-[10px] tracking-[0.1em] px-2.5 py-1.5 bg-[rgba(218,255,1,0.08)] border border-[rgba(218,255,1,0.2)] rounded-md"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {credit}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* PRO Affiliation */}
          {artist.pro && (
            <div className="flex items-center gap-2 pt-2">
              <Award size={14} className="text-[rgb(161,161,170)]" />
              <span className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                {artist.pro}
              </span>
              {artist.ipi && (
                <span className="text-[rgb(100,100,110)] text-[10px] font-mono ml-auto">
                  IPI: {artist.ipi}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const RosterPage = () => {
  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden" data-testid="roster-page">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10 sm:mb-12"
          style={{ fontFamily: "'Inter', sans-serif" }}
          data-testid="back-to-portal-link"
        >
          <ArrowLeft size={14} />
          BACK TO PORTAL
        </Link>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 sm:mb-16"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Shield size={12} className="mr-2" />
            EXCLUSIVE REPRESENTATION
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            THE <span className="text-[rgb(218,255,1)]">ROSTER</span>
          </h1>
          <p
            className="text-[rgb(161,161,170)] text-sm sm:text-base max-w-2xl leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Handpicked composers and producers delivering premium sync-ready content for 
            film, television, and advertising. Each artist has been vetted for broadcast-quality 
            production and 100% clearable catalogs.
          </p>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12 sm:mb-16"
        >
          {[
            { label: "COMPOSERS", value: rosterData.length.toString().padStart(2, '0'), icon: Music },
            { label: "BMI REGISTERED", value: "100%", icon: CheckCircle },
            { label: "ONE-STOP CLEARED", value: "100%", icon: Shield },
            { label: "ACTIVE BRIEFS", value: "03", icon: TrendingUp },
          ].map((stat, i) => (
            <div
              key={i}
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 sm:p-5 text-center"
            >
              <stat.icon size={18} className="text-[rgb(218,255,1)] mx-auto mb-2" />
              <p
                className="text-white text-xl sm:text-2xl font-bold"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {stat.value}
              </p>
              <p
                className="text-[rgb(100,100,110)] text-[9px] sm:text-[10px] tracking-[0.15em]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Roster Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16">
          {rosterData.map((artist, index) => (
            <RosterCard key={artist.id} artist={artist} index={index} />
          ))}
        </div>

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <Star size={16} className="text-[rgb(218,255,1)]" />
            <span
              className="text-[rgb(161,161,170)] text-xs tracking-[0.2em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              INTERESTED IN JOINING?
            </span>
            <Star size={16} className="text-[rgb(218,255,1)]" />
          </div>
          <h2
            className="text-white text-xl sm:text-2xl font-bold mb-3"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            WE'RE ACTIVELY RECRUITING TOP-TIER TALENT
          </h2>
          <p
            className="text-[rgb(161,161,170)] text-sm mb-6 max-w-lg mx-auto"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            If you have broadcast-quality production skills, a 100% clearable catalog, 
            and are ready to work with major networks and studios, we want to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/ingestion"
              data-testid="submit-catalog-cta"
              className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-xs font-semibold tracking-[0.1em] px-6 py-3 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)] transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              SUBMIT YOUR CATALOG
              <ExternalLink size={14} />
            </Link>
            <a
              href="mailto:leaver@itzgreenmusic.com?subject=Roster%20Inquiry"
              className="text-[rgb(218,255,1)] text-sm font-medium hover:underline"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              leaver@itzgreenmusic.com
            </a>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default RosterPage;
